import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { StyleSheet, View, type ViewProps } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withSequence,
  withTiming,
} from 'react-native-reanimated';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { colors } from '../../theme/colors';
import { tokens } from '../../theme/tokens';
import { Text } from './Text';

export type StatusBannerVariant = 'warning' | 'info' | 'danger';

interface StatusBannerProps extends Omit<ViewProps, 'children'> {
  variant?: StatusBannerVariant;
  message: string;
  leading?: ReactNode;
  trailing?: ReactNode;
  showDot?: boolean;
  multiline?: boolean;
}

const variantColors: Record<StatusBannerVariant, { border: string; dot: string; text: string }> = {
  warning: {
    border: colors.status.warning.border,
    dot: colors.status.warning.main,
    text: colors.status.warning.light,
  },
  info: {
    border: colors.primary.border,
    dot: colors.primary.main,
    text: colors.text.primary,
  },
  danger: {
    border: colors.status.error.border,
    dot: colors.status.error.main,
    text: colors.status.error.light,
  },
};

export function StatusBanner({
  variant = 'info',
  message,
  leading,
  trailing,
  showDot = true,
  multiline = false,
  style,
  ...rest
}: Readonly<StatusBannerProps>) {
  const palette = variantColors[variant];
  const reduced = useReducedMotion();
  const scale = useSharedValue(1);

  useEffect(() => {
    if (reduced || !showDot) {
      scale.value = 1;
      return;
    }
    scale.value = withRepeat(
      withSequence(
        withTiming(1.2, { duration: 620, easing: Easing.bezier(0.22, 1, 0.36, 1) }),
        withTiming(0.8, { duration: 620, easing: Easing.bezier(0.22, 1, 0.36, 1) }),
      ),
      -1,
      false,
    );
  }, [reduced, showDot, scale]);

  const dotStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }));

  return (
    <View
      style={[
        styles.container,
        multiline ? styles.containerMultiline : styles.containerSingleLine,
        { borderColor: palette.border },
        style,
      ]}
      accessibilityRole="alert"
      accessibilityLiveRegion="polite"
      {...rest}
    >
      {leading}
      {showDot && (
        <Animated.View
          style={[styles.dot, { backgroundColor: palette.dot }, dotStyle]}
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
        />
      )}
      <Text
        style={[styles.text, { color: palette.text }]}
        numberOfLines={multiline ? undefined : 1}
      >
        {message}
      </Text>
      {trailing}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: tokens.spacing.md,
    gap: tokens.spacing.sm,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    backgroundColor: 'transparent',
  },
  containerSingleLine: {
    height: 36,
  },
  containerMultiline: {
    minHeight: 36,
    paddingVertical: tokens.spacing.sm,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  text: {
    flex: 1,
    fontSize: tokens.fontSize.sm,
    fontWeight: tokens.fontWeight.medium,
  },
});
